import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, X } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";

// MCP baglayicisi (ChatGPT / Claude) icin OAuth onay ekrani. Sunucunun
// /oauth/authorize ucu buraya yonlendirir; panel oturumuyla onaylaninca
// istemciye kod ile geri donulur.

const PERMISSIONS = [
  "Ürün analizlerini ve önerilen başlık/etiketleri okumak",
  "Analizlerden AI tasarım üretmek (OpenRouter anahtarınla, ücretli)",
  "Otomatik custom liste hattını başlatmak",
  "Tasarımlardan liste taslağı oluşturmak",
  "Taslakların başlık, etiket ve açıklamasını güncellemek",
];

export default function OAuthAuthorizePage() {
  const navigate = useNavigate();
  const search = window.location.search;
  const [info, setInfo] = useState<{ clientName: string; redirectHost: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);

  useEffect(() => {
    if (!localStorage.getItem("erc_token")) {
      navigate(`/login?next=${encodeURIComponent(`/oauth/authorize${search}`)}`, { replace: true });
      return;
    }
    fetch(`${getBackendUrl()}/api/oauth/request${search}`, { headers: authHeaders() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (r.status === 401) { navigate(`/login?expired=1&next=${encodeURIComponent(`/oauth/authorize${search}`)}`, { replace: true }); return; }
        if (!r.ok) throw new Error(j.error || "Yetkilendirme isteği geçersiz");
        setInfo(j.data);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [navigate, search]);

  const decide = async (approve: boolean) => {
    setBusy(approve ? "approve" : "deny"); setError(null);
    try {
      const params = Object.fromEntries(new URLSearchParams(search));
      const r = await fetch(`${getBackendUrl()}/api/oauth/approve`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ ...params, approve }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Onay kaydedilemedi");
      window.location.href = j.data.redirect as string;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-indigo-600" />
          <h1 className="text-lg font-semibold text-gray-900">Bağlantı izni</h1>
        </div>
        {error && <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        {!info && !error && <div className="flex items-center gap-2 py-6 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> İstek doğrulanıyor…</div>}
        {info && (
          <>
            <p className="mb-1 text-sm text-gray-700">
              <b>{info.clientName}</b> panel hesabına erişmek istiyor.
            </p>
            <p className="mb-4 text-xs text-gray-500">Onaylarsan <b>{info.redirectHost}</b> adresine dönülecek.</p>
            <p className="mb-2 text-xs font-medium text-gray-600">Verilecek izinler</p>
            <ul className="mb-5 space-y-1.5">
              {PERMISSIONS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-gray-700"><span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-500" />{p}</li>
              ))}
            </ul>
            <p className="mb-5 text-xs text-gray-500">
              Etsy'de yayınlama, silme ve alıcıya mesaj gönderme izni <b>verilmez</b>. Bağlantıyı istediğin an ChatGPT / MCP sayfasından kesebilirsin.
            </p>
            <div className="flex gap-2">
              <button onClick={() => void decide(true)} disabled={busy !== null}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                {busy === "approve" && <Loader2 className="h-4 w-4 animate-spin" />} İzin ver
              </button>
              <button onClick={() => void decide(false)} disabled={busy !== null}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <X className="h-4 w-4" /> Reddet
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
